import totalUsers from '@v2/kpis/standardMetrics/userAdoption/totalUsers';
import activeUsers from '@v2/kpis/standardMetrics/userAdoption/activeUsers';
import adoptionRate from '@v2/kpis/standardMetrics/userAdoption/adoptionRate';
import averageTimeInPlatform from '@v2/kpis/standardMetrics/userAdoption/averageTimeInPlatform';
import uniquePageViews from '@v2/kpis/standardMetrics/userAdoption/uniquePageViews';
import uniqueUsers from '@v2/kpis/standardMetrics/userAdoption/uniqueUsers';
import valuableActionsTaken from '@v2/kpis/standardMetrics/userAdoption/valuableActionsTaken';
import { constants } from '../../../constants/Constants';

// Simple mapping of metric IDs to their handlers
export const metricsMap = {
  totalUsers: {
    module: totalUsers,
    kpiName: 'Total Users',
    definition:
      'The total number of new and active users for the selected period',
  },
  activeUsers: {
    module: activeUsers,
    kpiName: 'Active Users',
    definition:
      'The total number of users who have logged in at least once in the selected period.',
  },
  adoptionRate: {
    module: adoptionRate,
    kpiName: 'Adoption Rate',
    definition: 'Active Users in comparison to Total Users',
  },
  averageTimeInPlatform: {
    module: averageTimeInPlatform,
    kpiName: 'Average Time in Platform',
    definition: 'Average time user spends on the platform',
  },
  uniquePageViews: {
    module: uniquePageViews,
    kpiName: 'Unique Page Views',
    definition:
      'Number of unique pages visited by users within a specified period',
  },
  uniqueUsers: {
    module: uniqueUsers,
    kpiName: 'Unique Users',
    definition:
      'The number of individual sessions initiated by all users to the application during a specific time period',
  },
  valuableActionsTaken: {
    module: valuableActionsTaken,
    kpiName: 'Valuable Actions Taken',
    definition:
      'Aggregate number of times users took the following actions: Generate Calendar, Compare Calendar, Analyze Calendar, Adjust calendar, Create Planning Cycle.',
  },
};

// Product-specific metric configurations
export const productMetrics = {
  // Manufacturing Control Tower specific metrics
  [constants.products.ManufacturingControlTower]: {
    kpis: [
      'totalUsers',
      'activeUsers',
      'adoptionRate',
      'averageTimeInPlatform',
      'uniquePageViews',
      'uniqueUsers',
    ],
    definitionOverride: {
      uniqueUsers: {
        kpiName: 'Unique Sessions',
        definition:
          'The number of individual sessions initiated by all users to the application during a specific time period',
      },
    },
  },
  [constants.products.ProfitToServe]: {
    kpis: [
      'totalUsers',
      'activeUsers',
      'adoptionRate',
      'averageTimeInPlatform',
      'uniquePageViews',
      'uniqueUsers',
    ],
    definitionOverride: {
      totalUsers: {
        kpiName: 'Total Users',
        definition: 'The total number of users in selected period',
      },
      activeUsers: {
        kpiName: 'Active Users',
        definition:
          'Returning users that have logged into the application for the selected period.',
      },
      averageTimeInPlatform: {
        kpiName: 'Average Time in Platform',
        definition:
          'Average time user spends on the platform in the minutes and seconds.',
      },
      uniqueUsers: {
        kpiName: 'Unique Sessions',
        definition:
          'The number of individual sessions initiated by all users to the application during a specific time period.',
      },
    },
  },
  [constants.products.PricingAI]: {
    kpis: [
      'uniqueUsers',
      'activeUsers',
      'totalUsers',
      'adoptionRate',
      'averageTimeInPlatform',
      'uniquePageViews',
    ],
    kpiNameOverride: {
      averageTimeInPlatform: 'Median Time in Platform',
    },
    definitionOverride: {
      uniqueUsers: {
        kpiName: 'Unique Sessions',
        definition: 'Cumulative total unique user sessions.',
      },
      activeUsers: {
        kpiName: 'Active Users',
        definition:
          'Returning users that has logged into the application for the selected period.',
      },
      totalUsers: {
        kpiName: 'Total Users',
        definition:
          'The total number of new and active users for the selected period.',
      },

      averageTimeInPlatform: {
        kpiName: 'Median Time in Platform',
        definition: 'Median time user spends on the platform.',
      },
      uniquePageViews: {
        kpiName: 'Unique Page Views',
        definition:
          'Number of unique pages visited by users within a specified period.',
      },
    },
  },
  [constants.products.PromoAI]: {
    kpis: [
      'adoptionRate',
      'valuableActionsTaken',
      'averageTimeInPlatform',
      'uniqueUsers',
    ],
    kpiNameOverride: {
      averageTimeInPlatform: 'Median Time in Platform',
    },
    definitionOverride: {
      adoptionRate: {
        kpiName: 'Adoption Rate',
        definition: 'Active Users in comparison to Total Users.',
      },
      valuableActionsTaken: {
        kpiName: 'Valuable Actions Taken',
        definition:
          'Aggregate number of times users took the following actions: Generate Calendar, Compare Calendar, Analyze Calendar, Adjust calendar, Create Planning Cycle.',
      },
      averageTimeInPlatform: {
        kpiName: 'Median Time in Platform',
        definition: 'Median time user spends on the platform.',
      },
      uniqueUsers: {
        kpiName: 'Unique Sessions',
        definition: 'Cumulative total unique user sessions.',
      },
    },
  },
  [constants.products.o9Solutions]: {
    kpis: ['totalUsers', 'activeUsers', 'adoptionRate'],
    definitionOverride: {
      activeUsers: {
        kpiName: 'Active Users',
        definition: 'Number of users logging in or completing key actions.',
      },
      totalUsers: {
        kpiName: 'Total Users',
        definition: 'Total number of licensed users.',
      },
      adoptionRate: {
        kpiName: 'Adoption Rate',
        definition: 'Active Users as a percentage of total licensed users.',
      },
    },
    // Market-specific definition overrides for o9Solutions
    marketOverrides: Object.fromEntries(
      [
        constants.markets.turkey,
        constants.markets.france,
        constants.markets.usa,
      ].map((market) => [
        market,
        {
          totalUsers: {
            kpiName: 'Total Users',
            definition:
              'All the distinct licensed users which are tagged to one/multiple role(s).',
          },
          activeUsers: {
            kpiName: 'Active Users',
            definition:
              'Users of O9 assigned to one/multiple role and are following minimum usage criteria on monthly basis.',
          },
          adoptionRate: {
            kpiName: 'Adoption Rate',
            definition: 'Active Users as a percentage of total users.',
          },
        },
      ])
    ),
  },
  // Default metrics for all other products
  default: [
    'totalUsers',
    'activeUsers',
    'adoptionRate',
    'averageTimeInPlatform',
    'uniquePageViews',
    'uniqueUsers',
  ],
};

/**
 * Get default metrics for a specific product
 * @param productId The product identifier
 * @returns Array of default metric IDs for the product
 */
export const getDefaultMetrics = (productId: number): string[] => {
  if (productId && productMetrics[productId]) {
    return productMetrics[productId].kpis;
  }
  return productMetrics.default;
};

/**
 * Process multiple KPIs with the same data and product context
 * @param metricIds Array of metric identifiers to process
 * @param data Common data to pass to all handlers
 * @param product Selected product context
 * @returns Array of processed KPI results
 */
export const processMetrics = (
  metricIds: string[],
  data: any,
  product?: any
): any[] => {
  return metricIds
    .map((id) => {
      const actualExecutableFunction = metricsMap[id]?.module?.handler;
      if (typeof actualExecutableFunction !== 'function') {
        // If the handler is not found or is not a function, skip this metric.
        // You could add a console.warn here for debugging if needed.
        return null;
      }

      // Get the result from the handler function
      const result = actualExecutableFunction(data, product);

      // Apply kpiNameOverride if it exists for this metric
      if (result && product && product.productId) {
        const productConfig = productMetrics[product.productId];
        if (
          productConfig &&
          typeof productConfig === 'object' &&
          !Array.isArray(productConfig) &&
          productConfig.kpiNameOverride &&
          productConfig.kpiNameOverride[id]
        ) {
          result.title = productConfig.kpiNameOverride[id];
        }
      }

      return result;
    })
    .filter(Boolean);
};

export interface MetricDefinition {
  kpiName: string;
  definition: string;
}

export interface ProductMetricConfig {
  kpis: string[];
  definitionOverride?: Record<
    string,
    { kpiName?: string; definition?: string }
  >;
  kpiNameOverride?: Record<string, string>;
  marketOverrides?: Record<
    number,
    Record<string, { kpiName?: string; definition?: string }>
  >;
}

/**
 * Retrieves a list of metric definitions, applying product-specific and market-specific overrides if available.
 * @param productId Optional. The identifier of the product for which to get metric definitions.
 *                  This can be a number (e.g., from constants.products) or a string.
 * @param marketId Optional. The identifier of the market for which to get market-specific definitions.
 *                 This is used for products like o9Solutions that have market-specific overrides.
 * @returns An array of objects, each containing the kpiName and definition for a metric.
 */
export const getMetricDefinitions = (
  productId?: number | string,
  marketId?: number
): MetricDefinition[] => {
  const metricDefinitions: MetricDefinition[] = [];

  let orderedMetricIds: string[];
  const productConfig =
    productId !== undefined ? productMetrics[productId] : undefined;

  orderedMetricIds = productConfig?.kpis || productMetrics.default;

  // Ensure orderedMetricIds is an array (it should be if productMetrics.default is correctly an array)
  if (!Array.isArray(orderedMetricIds)) {
    orderedMetricIds = []; // Fallback to an empty list if productMetrics.default is somehow not an array
  }

  for (const metricId of orderedMetricIds) {
    if (Object.prototype.hasOwnProperty.call(metricsMap, metricId)) {
      let kpiName = metricsMap[metricId].kpiName;
      let definition = metricsMap[metricId].definition;

      // First check for market-specific overrides (highest priority)
      if (
        productConfig &&
        typeof productConfig === 'object' &&
        !Array.isArray(productConfig) &&
        productConfig.marketOverrides &&
        marketId !== undefined &&
        productConfig.marketOverrides[marketId] &&
        productConfig.marketOverrides[marketId][metricId]
      ) {
        const marketOverride =
          productConfig.marketOverrides[marketId][metricId];
        if (typeof marketOverride.kpiName === 'string') {
          kpiName = marketOverride.kpiName;
        }
        if (typeof marketOverride.definition === 'string') {
          definition = marketOverride.definition;
        }
      }
      // Fall back to product-level definitionOverride
      else if (
        productConfig &&
        typeof productConfig === 'object' &&
        !Array.isArray(productConfig) &&
        productConfig.definitionOverride &&
        productConfig.definitionOverride[metricId]
      ) {
        const override = productConfig.definitionOverride[metricId];
        if (typeof override.kpiName === 'string') {
          kpiName = override.kpiName;
        }
        if (typeof override.definition === 'string') {
          definition = override.definition;
        }
      }
      metricDefinitions.push({ kpiName, definition });
    }
  }
  return metricDefinitions;
};
